import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NzFormatEmitEvent, NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { Router } from '@angular/router';
import { ArbolGrupo, ArbolGrupoHijo, ArbolService } from '@app/services/arbol/arbol.service';
import { EmpresaCentralizadaService } from '@app/services/empresaCentralizada/empresaCentralizada.service';
import { StorageService } from '@global-service/storage';

@Component({ standalone: false, selector: 'dashboard-arbol', templateUrl: './arbol.component.html', styleUrls: ['./arbol.component.scss'] })
export class ArbolComponent implements OnInit {
  private readonly searchStorageKey = 'dashboard-arbol-search';
  @Output() onDblClickNode = new EventEmitter<any>();
  @Output() onHidden = new EventEmitter<boolean>();
  public loading = false;
  public searchFilterActive = false;
  public isHiddenBar = false;
  public searchValue = '';
  public expandedKeys: string[] = [];
  public treeNodes: NzTreeNodeOptions[] = [];
  private allTreeNodes: NzTreeNodeOptions[] = [];
  private filterTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly arbolService: ArbolService, private readonly storageService: StorageService, private readonly router: Router, private readonly empresaCentralizadaService: EmpresaCentralizadaService, private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.searchValue = sessionStorage.getItem(this.searchStorageKey) ?? '';
    this.loading = true;
    this.arbolService.init().then(response => {
      this.allTreeNodes = this.toTreeNodes(response as (ArbolGrupo | ArbolGrupoHijo)[]);
      this.treeNodes = this.allTreeNodes;
      if (this.searchValue) this.applyFilter();
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }, () => { this.loading = false; this.changeDetectorRef.detectChanges(); });
  }

  refrescarArbol(): void {
    this.loading = true;
    this.arbolService.getGrupos(true).then(response => {
      this.allTreeNodes = this.toTreeNodes(response);
      this.treeNodes = this.allTreeNodes;
      this.searchValue = '';
      sessionStorage.removeItem(this.searchStorageKey);
      this.expandedKeys = [];
      this.loading = false;
      this.router.navigate(['/dashboard']);
      this.changeDetectorRef.detectChanges();
    }, () => { this.treeNodes = []; this.loading = false; this.changeDetectorRef.detectChanges(); });
  }

  filterTree(filter: string): void {
    this.searchValue = filter.trim();
    if (this.searchValue) sessionStorage.setItem(this.searchStorageKey, this.searchValue);
    else sessionStorage.removeItem(this.searchStorageKey);
    if (this.filterTimer) clearTimeout(this.filterTimer);
    this.filterTimer = setTimeout(() => this.applyFilter(), 120);
  }
  toggleSearchFilter(): void { this.searchFilterActive = !this.searchFilterActive; }
  clickHiddenButton(): void { this.isHiddenBar = !this.isHiddenBar; this.onHidden.emit(this.isHiddenBar); }

  onTreeClick(event: NzFormatEmitEvent): void {
    if (!event.node) return;
    const data = event.node.origin.data;
    if (event.node.parentNode) this.storageService.setGrupoId(data.grupo_id);
    this.onDblClickNode.emit({ data });
    this.selectEmpresa(data);
  }

  onTreeExpand(event: NzFormatEmitEvent): void {
    const node = event.node;
    if (!node) return;
    this.expandedKeys = node.isExpanded
      ? [...new Set([...this.expandedKeys, node.key])]
      : this.expandedKeys.filter((key) => key !== node.key);
    if (!node?.isExpanded || node.children.length || node.isLeaf || node.isLoading) return;
    const data = node.origin.data as ArbolGrupoHijo;
    node.isLoading = true;
    this.arbolService.getSucursales(data.empresa_id).then(
      sucursales => node.addChildren(this.toTreeNodes(sucursales as ArbolGrupoHijo[])),
      () => node.isExpanded = false
    ).finally(() => {
      node.isLoading = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  private selectEmpresa(data: any): void {
    const serverData = data.server_data;
    let empresaId = null;
    if (data.type === 'sucursal') {
      empresaId = serverData?.empresa?.id;
    } else if (data.type === 'empresa_deipe') {
      empresaId = serverData?.id;
    }
    if (empresaId !== null && empresaId !== undefined) this.empresaCentralizadaService.setEmpresaSeleccionadaId(String(empresaId));
  }

  private applyFilter(): void {
    if (!this.searchValue) {
      this.treeNodes = this.allTreeNodes;
      this.expandedKeys = [];
      return;
    }

    const expandedKeys: string[] = [];
    this.treeNodes = this.filterNodes(this.allTreeNodes, this.searchValue.toLocaleLowerCase(), expandedKeys);
    this.expandedKeys = expandedKeys;
  }

  private filterNodes(nodes: NzTreeNodeOptions[], filter: string, expandedKeys: string[]): NzTreeNodeOptions[] {
    return nodes.reduce((matches, node) => {
      const children = node.children ? this.filterNodes(node.children, filter, expandedKeys) : [];
      const title = String(node.title).toLocaleLowerCase();
      if (title.includes(filter) || children.length > 0) {
        if (children.length > 0) expandedKeys.push(node.key);
        matches.push({ ...node, children: children.length > 0 ? children : node.children });
      }
      return matches;
    }, [] as NzTreeNodeOptions[]);
  }

  private toTreeNodes(nodes: (ArbolGrupo | ArbolGrupoHijo)[]): NzTreeNodeOptions[] {
    return nodes.map((node, index) => {
      const data = node as ArbolGrupo & ArbolGrupoHijo;
      const children = Array.isArray(data.children) ? this.toTreeNodes(data.children as ArbolGrupoHijo[]) : [];
      return { key: `${data.type}-${data.grupo_id}-${data.empresa_id ?? data.codigo ?? index}`, title: data.name, isLeaf: !data.children && data.type !== 'empresa_deipe', children, data } as NzTreeNodeOptions;
    });
  }
}
